import { trackGaLink } from '@propertypal/shared/src/services/analytics';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import { displayConsentUi } from '../../services/ads/cmp';
import CopyrightAndPolicyLinks from './copyright-and-policy-links/CopyrightAndPolicyLinks';
import { ButtonLink, FooterContainer, FooterInner, Links, LinksColumn } from './Footer.style';
import SiteMapFooter from './site-map-footer/SiteMapFooter';
import SocialLinks from './social-links/SocialLinks';

export interface ListItem {
  url: string;
  label: string;
}

export interface SiteMapData {
  parent: ListItem;
  children: ListItem[];
}

interface Props {
  siteMapData: SiteMapData[] | null;
  cfIpCountry: string | null;
}

const Footer: React.FunctionComponent<Props> = (props) => {
  const router = useRouter();

  return (
    <FooterContainer className="hide-print">
      <PageWrapper noMaxWidth>
        <FooterInner>
          <Links>
            <LinksColumn>
              <h4>PROPERTYPAL.COM</h4>

              <ul>
                <li>
                  <Link href="/contact" prefetch={false}>
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link href="/find-an-agent" prefetch={false}>
                    Find an Agent
                  </Link>
                </li>
                <li>
                  <Link href="/site-map" prefetch={false}>
                    Sitemap
                  </Link>
                </li>
                <li>
                  <Link href="/careers" prefetch={false}>
                    Careers
                  </Link>
                </li>
                <li>
                  <a href="https://connect.propertypal.com/solutions/" target="_blank" rel="noreferrer">
                    FAQs
                  </a>
                </li>
                <li>
                  <Link href="/mobile-apps" prefetch={false}>
                    Mobile Apps
                  </Link>
                </li>
                <li>
                  <Link
                    href="/premium"
                    prefetch={false}
                    role="link"
                    onClick={trackGaLink('premium_click', 'Footer')}
                    onKeyDown={trackGaLink('premium_click', 'Footer')}
                    tabIndex={-1}
                  >
                    Promote Your Property
                  </Link>
                </li>
                <li>
                  <button onClick={displayConsentUi} type="button">
                    Change privacy settings
                  </button>
                </li>
              </ul>
            </LinksColumn>

            {props.cfIpCountry !== 'IE' && (
              <LinksColumn data-testid="mortgage-column">
                <h4>PROPERTYPAL MORTGAGES</h4>

                <ul>
                  <li>
                    <Link href="/mortgages/affordability/1" prefetch={false}>
                      Affordability Calculator
                    </Link>
                  </li>
                  <li>
                    <Link href="/mortgages/pre-approval/1" prefetch={false}>
                      Mortgage Pre-Approval
                    </Link>
                  </li>
                  <li>
                    <Link href="/mortgages/beat-your-bank/1" prefetch={false}>
                      Beat your bank
                    </Link>
                  </li>
                  <li>
                    <Link href="/mortgages/compare-mortgages" prefetch={false}>
                      Compare Mortgages
                    </Link>
                  </li>
                  {router.asPath.startsWith('/mortgages') && (
                    <li>
                      <a href="https://content.propertypal.com/propertypal-mortgages-complaints/">
                        Mortgages Complaints
                      </a>
                    </li>
                  )}
                </ul>
              </LinksColumn>
            )}

            <LinksColumn>
              <h4>PROFESSIONAL</h4>

              <ul>
                <li>
                  <Link href="/development-microsites" prefetch={false}>
                    Development Microsites
                  </Link>
                </li>
                <li>
                  <Link href="/estate-agent-websites" prefetch={false}>
                    Estate Agent Websites
                  </Link>
                </li>
                {props.cfIpCountry !== 'IE' && (
                  <li>
                    <Link href="/agent-advantage" prefetch={false}>
                      Agent Advantage
                    </Link>
                  </li>
                )}

                <li>
                  <a href="https://content.propertypal.com/agent-faq/" target="_blank" rel="noreferrer">
                    Agents FAQs
                  </a>
                </li>

                <li>
                  <Link href="/estate-agent-information" prefetch={false}>
                    <ButtonLink>Estate Agents Login</ButtonLink>
                  </Link>
                </li>
              </ul>
            </LinksColumn>

            <LinksColumn>
              <h4>EDUCATION &amp; INSIGHTS</h4>

              <ul>
                {props.cfIpCountry !== 'IE' && (
                  <>
                    <li>
                      <Link href="/price-tracker" prefetch={false}>
                        Area Price Tracker
                      </Link>
                    </li>

                    <li>
                      <Link href="/school-finder" prefetch={false}>
                        School Finder
                      </Link>
                    </li>
                  </>
                )}

                {props.cfIpCountry === 'IE' && (
                  <li>
                    <Link href="/soldprice-checker" prefetch={false}>
                      Sold Price Checker
                    </Link>
                  </li>
                )}

                <li>
                  <a href="https://content.propertypal.com/">Insights</a>
                </li>
              </ul>
            </LinksColumn>
          </Links>
          <SocialLinks />
        </FooterInner>
      </PageWrapper>

      {props.siteMapData && props.siteMapData?.length > 0 && <SiteMapFooter siteMapData={props.siteMapData} />}

      <PageWrapper>
        <CopyrightAndPolicyLinks />
      </PageWrapper>
    </FooterContainer>
  );
};

export default Footer;
